/**
 * Sri Sai Pooja Samagri (SSPS) - Reusable Card Add-to-Cart State Controller
 *
 * Requirements:
 * 1. Single CTA: "Add to Cart" (or responsive "Add" / icon based on available card space).
 * 2. In-button feedback state machine:
 *    - State 1: READY: [cart icon] Add to Cart
 *    - State 2: SUBMITTING: [spinner] Adding... (prevent duplicate clicks)
 *    - State 3: SUCCESS: [animated check] Added (subtle muted green, duration ~1100ms)
 *    - State 4: ERROR: [warning icon] Try Again (briefly ~1500ms, then restores READY)
 * 3. Repeat add-to-cart: Clicking again adds another +1 for the same variant and increments cart count.
 * 4. Zero popups, zero snackbars, zero toasts, zero drawer auto-open, zero page navigation.
 * 5. Authoritative synchronization of all cart badges globally.
 */
(function () {
  'use strict';

  let cartData = null;
  let isFetchingCart = false;
  const inFlightVariants = new Set();

  // Trigger tactile bounce animation on all cart badges
  function triggerBadgeBounce() {
    const badges = document.querySelectorAll(
      '#cart-notification-count, #cart-notification-count-mobile, .mobile__nav-badge, .cart-count-bubble'
    );
    badges.forEach((b) => {
      b.classList.remove('ssps-badge-bounce');
      void b.offsetWidth; // force reflow
      b.classList.add('ssps-badge-bounce');
    });
  }

  // Update all cart count badges across desktop header, mobile bottom navigation, and drawers
  function updateCartBadges(count) {
    const badges = document.querySelectorAll(
      '#cart-notification-count, #cart-notification-count-mobile, .mobile__nav-badge, .cart-count-bubble'
    );
    const num = Math.max(0, Number(count) || 0);
    badges.forEach((badge) => {
      badge.textContent = String(num);
      if (num > 0) {
        badge.style.display = '';
        badge.classList.remove('d-none');
      } else {
        badge.style.display = 'none';
        badge.classList.add('d-none');
      }
    });
  }

  // Fetch full cart state from Shopify to guarantee zero drift
  async function refreshCart() {
    if (isFetchingCart) return;
    isFetchingCart = true;
    try {
      const res = await fetch('/cart.js', {
        headers: {
          'Accept': 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        }
      });
      if (!res.ok) throw new Error('Cart fetch failed');
      cartData = await res.json();
      updateCartBadges(cartData.item_count);
      document.dispatchEvent(new CustomEvent('cart:updated', { detail: { cart: cartData } }));
    } catch (err) {
      console.warn('[SSPS Cart] Error fetching cart:', err);
    } finally {
      isFetchingCart = false;
    }
  }

  // Global helper for toast suppression & badge updates
  window.SSPSCartFeedback = {
    show: function () {
      // Toast strictly disabled - visual success is inside button itself
    },
    updateCount: function (count) {
      updateCartBadges(count);
      triggerBadgeBounce();
    }
  };

  // Eliminate any third-party toast elements injected into DOM (e.g. Shop Pay toast)
  if (typeof MutationObserver !== 'undefined') {
    new MutationObserver((mutations) => {
      for (const m of mutations) {
        for (const node of m.addedNodes) {
          if (node.nodeType === 1) {
            const tag = node.tagName ? node.tagName.toLowerCase() : '';
            const cls = typeof node.className === 'string' ? node.className : '';
            if (
              tag === 'shop-toast' ||
              cls.includes('shop-toast') ||
              cls.includes('toast') ||
              node.id === 'ssps-cart-toast' ||
              (node.id && node.id.includes('toast'))
            ) {
              node.remove();
            }
          }
        }
      }
    }).observe(document.documentElement, { childList: true, subtree: true });
  }

  // Handle Add to Cart button click on product cards
  async function handleCardAdd(btn) {
    if (!btn || btn.disabled) return;
    if (btn.classList.contains('is-submitting')) return; // Prevent duplicate clicks

    const variantId = btn.dataset.variantId || btn.closest('[data-variant-id]')?.dataset.variantId;
    if (!variantId) {
      console.error('[SSPS Cart] Missing variant ID on card button', btn);
      return;
    }

    const vIdStr = String(variantId);
    if (inFlightVariants.has(vIdStr)) return;
    inFlightVariants.add(vIdStr);

    // State 2: SUBMITTING ("Adding...")
    btn.classList.remove('is-success', 'is-error');
    btn.classList.add('is-submitting');

    try {
      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        },
        body: JSON.stringify({
          items: [{
            id: Number(variantId),
            quantity: 1
          }],
          sections: 'mini-cart-drawer,cart-notification-count'
        })
      });

      if (!response.ok) {
        const errJson = await response.json().catch(() => ({}));
        throw new Error(errJson.description || errJson.message || 'Could not add to cart');
      }

      const result = await response.json();

      // Silently update mini-cart drawer HTML without opening it
      if (result.sections && result.sections['mini-cart-drawer']) {
        const drawer = document.getElementById('mini-cart-drawer');
        if (drawer) {
          const parsed = new DOMParser().parseFromString(result.sections['mini-cart-drawer'], 'text/html');
          const newContent = parsed.getElementById('mini-cart-drawer');
          if (newContent) drawer.innerHTML = newContent.innerHTML;
        }
      }

      // State 3: SUCCESS ("Added" with animated checkmark)
      btn.classList.remove('is-submitting');
      btn.classList.add('is-success');

      // Update badges
      await refreshCart();
      triggerBadgeBounce();

      // After 1100ms, restore READY state so user can add again
      setTimeout(() => {
        btn.classList.remove('is-success');
      }, 1100);

    } catch (err) {
      console.error('[SSPS Cart] Add to cart error:', err);
      // State 4: ERROR ("Try Again")
      btn.classList.remove('is-submitting');
      btn.classList.add('is-error');

      setTimeout(() => {
        btn.classList.remove('is-error');
      }, 1500);
    } finally {
      inFlightVariants.delete(vIdStr);
    }
  }

  // Delegated event listener for all card Add-to-Cart buttons
  document.addEventListener('click', function (event) {
    const btn = event.target.closest('.ssps-card-add-btn, .product__card--cart-btn, .deals--product-cart-button');
    if (!btn) return;

    // Do NOT intercept if inside main product PDP form or quick view form (handled by product-form.js)
    if (btn.closest('product-form') || btn.closest('.product_buy_button_form') || btn.closest('#quickViewWrapper')) {
      return;
    }

    event.preventDefault();
    event.stopPropagation();
    handleCardAdd(btn);
  });

  // Initial cart count sync on load
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', refreshCart);
  } else {
    refreshCart();
  }
})();
